import { AppConfig } from 'src/app/app.config';
import { ClientEntity } from './../../models/clients';
import { GeneralSelectionFacade } from './../../store/general-selection/facade/general-selection.facade';
import { InvestmentPlan } from 'src/app/models/orders';
import { AccountVM } from 'src/app/models/account';
import {filter, map, startWith} from 'rxjs/operators';
import { TradingFacades } from 'src/app/store/order/facades/trading.facades';
import { UserFacade } from 'src/app/store/user/facade/user.facade';
import { TokenService } from 'src/app/services/token/token.service';
import { Injectable } from '@angular/core';
import { zip, Observable, Subscription, combineLatest } from 'rxjs';
import { initialTradingState } from 'src/app/store/state/order.state';
import { AuthFacade } from 'src/app/store/auth/facade/auth.facade';
import { isEmptyObject } from 'src/app/utils/empty-object-check';
import { VerificationStatus } from 'src/app/models/lookups/VerificationStatus';
import { InvestorStatus } from 'src/app/models/investor';
import { Features } from 'src/app/models/features';
import {InvestorFacade} from "../../store/investor/facade/investor.facade";
import { RegularInvestmentPlanFacade } from 'src/app/store/regular-investment-plan/regular-investment-plan.facade';

type intercomProperties = [any, AccountVM, Partial<InvestmentPlan>, ClientEntity, InvestorStatus];

interface IntercomMemberData {
  email: string;
  name: string;
  memberId: number;
  accounts: string; // this property refers to entity id's an account can be linked to. We only send entity id's that are related to the site id, so no entity id's from other cleitns will be exposed
  authoriser: boolean;
  mobile: string;
  phone: string;
  verificationStatus: string;
}
interface IntercomInvestorData {
  investorId: number;
  investName: string;
  hasInvestmentPlan: boolean;
  netAssetValue: number;
  status: string;
}

@Injectable()
export class IntercomService {

  private intercomPropertiesSubscription: Subscription;
  private readonly intercomAppId: string;

  // We provide each subscriber here a default value. This is to ensure that in the event one of them fails to resolve, 
  // the user still has access to Intercom.
  private investmentPlan$: Observable<Partial<InvestmentPlan>> = this.regularInvestmentFacade.getRegularInvestmentPlan().pipe(
    filter((ip) => ip !== initialTradingState.investmentPlan),
      startWith({})
  );

  private userProfileComplete$: Observable<any> = this.userFacade.userProfile$.pipe(
    filter((profile: AccountVM) => {
      return !!profile?.userEntityId;
    }), startWith({})
  );

  private selectedUserEntity$: Observable<any> = this.generalSelectionFacade.getSelectedInvestorEntities().pipe(
    filter((selectedUserEntities: ClientEntity[]) => {
      return selectedUserEntities?.length > 0;
    }),
    map((selectedUserEntities: ClientEntity[]) => {
      return selectedUserEntities[0];
    }), startWith({})
  );

  private investorStatus$ = this.investorFacade.investorStatus$.pipe(
    filter((investorStatus: InvestorStatus) => {
      return !!investorStatus;
    }), startWith({} as InvestorStatus)
  );
  
  private intercomPropertiesProvider$: Observable<intercomProperties> = combineLatest([
    this.tokenService.claimsNotNull$,
    this.userProfileComplete$,
    this.investmentPlan$,
    this.selectedUserEntity$,
    this.investorStatus$
  ]);

  constructor(
    private appConfig: AppConfig,
    private tokenService: TokenService,
    private userFacade: UserFacade,
    private authFacade: AuthFacade,
    private tradingFacade: TradingFacades,
    private generalSelectionFacade: GeneralSelectionFacade,
    private investorFacade: InvestorFacade,
    private regularInvestmentFacade: RegularInvestmentPlanFacade) {
    this.intercomAppId = (this.appConfig.getFeatureProperty(Features.INTERCOM, 'intercomAppId'));
    this.setisLoggedInSubscription();
  }

  private setisLoggedInSubscription() {
    this.authFacade.userLoggedIn$().subscribe((isLoggedIn: boolean) => {
      if (isLoggedIn) {
        this.setIntercomPropertiesSubscription();
      } else {
        this.endSession();
      }
    });
  }

  private setIntercomPropertiesSubscription() {
    this.intercomPropertiesSubscription = this.intercomPropertiesProvider$.subscribe((properties: intercomProperties) => {
      this.submitDataToIntercom(properties);
    });
  }

  private submitDataToIntercom(properties: intercomProperties) {
    const [claims, userProfile, investmentPlan, selectedUserEntity, investorStatus] = properties;
    const investorData = this.getInvestorData(investmentPlan, selectedUserEntity, investorStatus);
    const memberData = this.getMemberData(claims, userProfile);
    this.startSession(memberData);
    this.update(memberData, investorData);
  }

  private getInvestorData(investmentPlan: Partial<InvestmentPlan>, selectedUserEntity: ClientEntity, investorStatus: InvestorStatus): IntercomInvestorData {
    const totalValue = selectedUserEntity?.NetAssetValue?.Value;
    const investorData: IntercomInvestorData = {
      investorId: selectedUserEntity.EntityId,
      investName: selectedUserEntity.Name,
      hasInvestmentPlan: !isEmptyObject(investmentPlan),
      netAssetValue: totalValue,
      status: InvestorStatus[investorStatus],
    };

    return investorData;
  }


  private getMemberData(claims: any, userProfile: AccountVM): IntercomMemberData {

    let roles: string[] = [];
    if (Array.isArray(claims['role'])) {
      roles = claims['role'] as string[];
    } else if(Object.values(claims).indexOf('role') > -1) {
      roles.push(claims['role']);
    }

    const accounts: string[] = roles.map(role => {
      const items = role.split(',');
      return items[0];
    });

    let userScopes: string[] = [];
    if (Array.isArray(claims['user-scope'])) {
      userScopes = claims['user-scope'] as string[];
    } else {
      userScopes.push(claims['user-scope']);
    }

    const authoriser: string = userScopes.find(scope => scope === 'Authorizer');
    const phone: string = userProfile?.phone;
    const mobile: string = userProfile?.mobilePhone;
    const verificationStatus: string = (userProfile?.verificationStatus) ?  VerificationStatus[userProfile.verificationStatus] : '';


    const memberData: IntercomMemberData = {
      email: claims['unique_name'],
      name: claims['given_name'],
      memberId: parseInt(claims['primarysid'], 10),
      accounts: accounts.join(),
      authoriser: authoriser ? true : false,
      phone,
      mobile,
      verificationStatus
    };

    return memberData;
  }


  private startSession(data: IntercomMemberData): void {
    const d = new Date();
    window["intercomSettings"] = {
      app_id: this.intercomAppId,
      name: data.name,
      email: data.email,
      created_at: d.getTime(),
    };
  }

  private update(memberData: IntercomMemberData, investorData: IntercomInvestorData): void {
    const d = new Date();
    window["Intercom"]('boot', {
      app_id: this.intercomAppId,
      name: memberData.name,
      email: memberData.email,
      user_id: memberData.memberId,
      'mobile': memberData.mobile,
      'phone': memberData.phone,
      'verification_status': memberData.verificationStatus,
      created_at: d.getTime(),
      'investor_id': investorData.investorId,
      'investor_name': investorData.investName,
      'investment_plan': investorData.hasInvestmentPlan,
      'net_asset_value': investorData.netAssetValue,
      'accounts': memberData.accounts,
      'authoriser': memberData.authoriser,
      'account_status': investorData.status
    });
  }

  private endSession(): void {
    window['Intercom']('shutdown');
    if(this.intercomPropertiesSubscription){
      this.intercomPropertiesSubscription.unsubscribe();
    }
  }

}
